import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ChangeEvent,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { HoldingClass } from 'types';

import errorSwal from 'utils/errorSwal';

import successSwal from 'utils/successSwal';
import actionTypes from 'constants/actionTypes';
import { cleanValue } from 'constants/formats';

import { setModal } from 'modules/_shared/redux/modalActions';
import {
  createHoldingClass,
  updateHoldingClass,
} from 'modules/partners/redux/holdingClassActions';

import { addHoldingClassModalInitValue } from '../constants/addHoldingClassModalInitValue';
import { AddHoldingClassModalContextType } from '../types/AddHoldingClassModalContextType';
import { useGetNumerationOptions } from '../hooks/useGetNumerationOptions';
import { ownNumerationOption } from '../constants/ownNumerationOption';

type AddHoldingClassModalContextProps = {
  children: ReactNode;
  modalActionType: string;
  holdingClassToEdit?: HoldingClass;
};

const AddHoldingClassModalContext =
  createContext<AddHoldingClassModalContextType>(addHoldingClassModalInitValue);

export const AddHoldingClassModalProvider: FC<
  AddHoldingClassModalContextProps
> = ({ children, modalActionType, holdingClassToEdit }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const [name, setName] = useState<string>(holdingClassToEdit?.name || '');
  const [votes, setVotes] = useState<number>(holdingClassToEdit?.votes || 0);
  const [lockupPeriod, setLockupPeriod] = useState<string | null>(
    holdingClassToEdit?.lockupPeriod
      ? new Date(holdingClassToEdit.lockupPeriod).toISOString().split('T')[0]
      : null
  );
  const [conversionRate, setConversionRate] = useState<number>(
    holdingClassToEdit?.conversionRate || 0
  );
  const [nominalValue, setNominalValue] = useState<number>(
    holdingClassToEdit?.nominalValue ?? actualSociety?.nominalValue ?? 0
  );
  const [hasConversionRights, setHasConversionRights] = useState<boolean>(
    holdingClassToEdit?.hasConversionRights || false
  );
  const [hasLiquidationRights, setHasLiquidationRights] = useState<boolean>(
    holdingClassToEdit?.hasLiquidationRights || false
  );
  const [hasAntidilutionRights, setHasAntidilutionRights] = useState<boolean>(
    holdingClassToEdit?.hasAntidilutionRights || false
  );

  const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    data: numerationData,
    actions: numerationActions,
    isLoading: isLoadingNumerationOptions,
  } = useGetNumerationOptions({
    societyId: actualSociety?._id || '',
    numerationClassId: (holdingClassToEdit?.numerationOption as HoldingClass)
      ?._id,
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const onChangeName = (value: string) => {
    setName(value);
    setHasBeenEdited(true);
  };

  const handleChangeVotes = (event: ChangeEvent<HTMLInputElement>) => {
    const value = cleanValue(event.target.value);
    setVotes(Number(value));
    setHasBeenEdited(true);
  };

  const handleChangeLockupPeriod = (event: ChangeEvent<HTMLInputElement>) => {
    setLockupPeriod(event.target.value);
    setHasBeenEdited(true);
  };

  const handleChangeConversionRate = (event: ChangeEvent<HTMLInputElement>) => {
    const value = cleanValue(event.target.value);
    setConversionRate(value);
    setHasBeenEdited(true);
  };

  const handleChangeNominalValue = (event: ChangeEvent<HTMLInputElement>) => {
    const value = cleanValue(event.target.value);
    setNominalValue(value);
    setHasBeenEdited(true);
  };

  const handleChangeHasConversionRights = () => {
    setHasConversionRights((prev) => !prev);
    setHasBeenEdited(true);
  };

  const handleChangeHasLiquidationRights = () => {
    setHasLiquidationRights((prev) => !prev);
    setHasBeenEdited(true);
  };

  const handleChangeHasAntidilutionRights = () => {
    setHasAntidilutionRights((prev) => !prev);
    setHasBeenEdited(true);
  };

  const handleSaveHoldingClass = async () => {
    try {
      setIsLoading(true);

      const numerationOption =
        numerationData.selectedNumerationClass === ownNumerationOption.value
          ? null
          : numerationData.selectedNumerationClass;

      const newHoldingClass = {
        name,
        votes,
        numerationOption,
        lockupPeriod: lockupPeriod ? new Date(lockupPeriod) : null,
        nominalValue,
        conversionRate,
        hasConversionRights,
        hasLiquidationRights,
        hasAntidilutionRights,
        society: actualSociety['_id'],
      };

      if (modalActionType === actionTypes.EDIT) {
        await store.dispatch(
          updateHoldingClass({
            holdingClassId: holdingClassToEdit?._id || '',
            updateHoldingClassData: newHoldingClass,
          })
        );
      } else {
        await store.dispatch(
          createHoldingClass({ holdingClass: newHoldingClass })
        );
      }

      setIsLoading(false);

      const successMessage =
        modalActionType === actionTypes.EDIT
          ? t('ClassUpdatedSuccessfully')
          : t('ClassAddedSuccessfully');

      successSwal(successMessage);
      handleCloseModal();
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t('ErrorUpdatingClass')
          : t('ErrorAddingClass');

      errorSwal(errorMessage);
      handleCloseModal();
    }
  };

  return (
    <AddHoldingClassModalContext.Provider
      value={{
        data: {
          name,
          votes,
          selectedNumerationClass: numerationData.selectedNumerationClass,
          selectedNumerationClassOption:
            numerationData.selectedNumerationClassOption,
          numerationClassOptions: numerationData.numerationOptions,
          lockupPeriod,
          conversionRate,
          nominalValue,
          hasConversionRights,
          hasLiquidationRights,
          hasAntidilutionRights,
          modalActionType,
          hasBeenEdited,
          isLoading: isLoading || isLoadingNumerationOptions,
        },
        actions: {
          handleCloseModal,
          onChangeName,
          handleChangeVotes,
          handleChangeNumerationClassOption:
            numerationActions.handleChangeNumerationClassOption,
          handleFetchHoldingClassByTerm:
            numerationActions.handleFetchHoldingClassByTerm,
          handleChangeLockupPeriod,
          handleChangeConversionRate,
          handleChangeNominalValue,
          handleChangeHasConversionRights,
          handleChangeHasLiquidationRights,
          handleChangeHasAntidilutionRights,
          handleSaveHoldingClass,
        },
      }}
    >
      {children}
    </AddHoldingClassModalContext.Provider>
  );
};

export const useAddHoldingClassModal = () => {
  const context = useContext(AddHoldingClassModalContext);
  if (!context) {
    throw new Error(
      'useAddHoldingClassModal must be used within a AddHoldingClassModalProvider'
    );
  }
  return context;
};
